import IconButton from '@material-ui/core/IconButton';
import type { LabelDisplayedRowsArgs } from '@material-ui/core/TablePagination';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import Rating from '@material-ui/lab/Rating';
import Alert from 'components/Alert';
import MyTable, { MyTableBody, MyTableCol, MyTableHeader, MyTableRow, MyTablePagination } from 'components/MyTable';
import Spin from 'components/Spin';
import moment from 'moment';
import React, { memo, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useObservable } from 'react-use-observable';
import { tap } from 'rxjs/operators';
import Truncate from 'v2/components/Shared/TruncateText';
import producerRatingService from 'v2/services/rating/producer';

import type { IProducerRating } from 'v2/interfaces/rating';

import styles from './styles';

const List = memo(() => {
  useObservable(() => producerRatingService.loadRatings(), []);
  const classes = styles();
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);
  const [isLoading] = useObservable(() => producerRatingService.isLoading(), []);

  const [data] = useObservable(() => producerRatingService.getRatings(), []);

  const [pagination] = useObservable(() => producerRatingService.getPaginationResponse(), []);

  const indexes = [
    { title: 'Cliente', ordering: false },
    { title: 'Data', ordering: false },
    { title: 'Avaliação', ordering: false },
    { title: 'Comentário', ordering: false },
    { title: '', ordering: false },
  ];

  const handleChangePage = (_: unknown, page: number) => {
    const current_page = page + 1;
    producerRatingService.setPagination({ ...pagination, ...{ current_page } });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const per_page = Number(event.target.value);
    producerRatingService.setPagination({ ...pagination, ...{ per_page, current_page: 1 } });
  };

  const handleClickModerate = useCallback(async (rating: IProducerRating) => {
    setIsConfirmationOpen(true);
    const confirm = await Alert.show({
      message: `Deseja realmente tornar essa avaliação ${rating.moderated ? 'invisível' : 'visível'} na vitrine?`,
      title: `Tornar ${rating.moderated ? 'invisível' : 'visível'} na vitrine`,
      confirmation: true,
      buttonOk: 'Sim',
      buttonCancel: 'Não',
    });

    if (!confirm) {
      setIsConfirmationOpen(false);
      return;
    }
    producerRatingService
      .updateRating({ ...rating, ...{ moderated: !rating.moderated } })
      .pipe(
        tap(() => {
          toast.success('Avaliação atualizada com sucesso');
          producerRatingService.updateListRatings();
        })
      )
      .subscribe();
    setIsConfirmationOpen(false);
    return;
  }, []);

  if (isLoading) {
    return (
      <div className={classes.list}>
        <Spin />
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className={classes.list}>
        <Typography className={classes.notFound} variant='body1'>
          Nenhuma avaliação encontrada.
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.list}>
      <MyTable>
        <MyTableHeader index={indexes} />

        <MyTableBody>
          {data?.map((item: IProducerRating) => (
            <MyTableRow key={`item-${item.payload_id}`}>
              <MyTableCol screenSizes={{ xs: 12, sm: 6 }}>{item.rater}</MyTableCol>
              <MyTableCol screenSizes={{ xs: 6, sm: 3 }} className={classes.ratingColData}>
                {moment(item.rated_at).format('L')}
              </MyTableCol>
              <MyTableCol screenSizes={{ xs: 6, sm: 3 }} className={classes.ratingColRating}>
                <Rating value={Number(item.rating)} size='small' precision={0.5} readOnly />
                <p style={{ paddingLeft: 6 }}>{item.rating} / 5</p>
              </MyTableCol>
              <MyTableCol screenSizes={{ xs: 12, sm: 11 }} className={classes.ratingColComment}>
                <Truncate
                  style={{
                    width: '100%',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    maxWidth: '75vw',
                  }}
                  count={120}
                  final='...'
                  title={item.comment}
                >
                  {item.comment}
                </Truncate>
              </MyTableCol>
              <MyTableCol numeric action screenSizes={{ xs: 12, sm: 1 }} className={classes.options}>
                <Tooltip placement='top' title={`Tornar ${item.moderated ? 'invisível' : 'visível'} na vitrine`}>
                  <IconButton
                    color={item.moderated ? 'secondary' : 'primary'}
                    onClick={() => handleClickModerate(item)}
                    disabled={isConfirmationOpen}
                    size='small'
                  >
                    {item.moderated ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </Tooltip>
              </MyTableCol>
            </MyTableRow>
          ))}
        </MyTableBody>
      </MyTable>

      <MyTablePagination
        onPageChange={handleChangePage}
        rowsPerPage={pagination.per_page}
        count={pagination.total_items}
        page={pagination.current_page - 1}
        labelDisplayedRows={({ from, to, count }: LabelDisplayedRowsArgs) => `${from}-${to} de ${count}`}
        labelRowsPerPage='Itens por página:'
        onChangeRowsPerPage={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 20, 30, 40]}
      />
    </div>
  );
});

export default List;
